import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PageHeader } from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.tsx";
import useMarketingMaterialsCart from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import {
	CheckoutContainer,
	CheckoutHeaderItem,
	DetailsBox,
	DetailsBoxTitle,
	SectionTitle
} from "features/MarketingMaterials/pages/Checkout/Checkout.styles.ts";
import ProgressStepper from "components/Common/Stepper/Stepper.tsx";
import {
	ReviewButton,
	ShippingDetailsAddAddressButton,
	ShippingDetailsAddressInput,
	ShippingDetailsContent,
	ShippingDetailsInputs
} from "features/MarketingMaterials/pages/Checkout/CartDetailsShippingPage.styles.ts";
import {
	SectionCaption,
	SectionDescription
} from "components/CustomQuote/DeclineRequestModal/DeclineRequestModal.styles.ts";
import { Button, TextField } from "@mui/material";
import { RequiredFieldNotice, ShipToFormUsingInfoText } from "constants/text.ts";
import { formatPhoneNumber } from "components/Replacements/ReplacementsUtil.ts";
import { SelectAnAddress } from "components/NewOrders/constants.ts";
import { getFormFieldProps } from "utils/form.tsx";
import { models } from "types/api/viewModels.ts";
import {
	CART_JOB_NAME_MAX_LENGTH,
	CART_PO_NUMBER_MAX_LENGTH,
	type CartShippingDetailsFormValues,
	cartShippingDetailsSchema
} from "features/MarketingMaterials/pages/Checkout/CartShippingDetails.schema.ts";
import { useNavigate } from "react-router";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import AddNewAddressModal from "pages/Replacements/components/AddNewAddressModal/AddNewAddressModal.tsx";
import AddressFormProvider from "pages/Replacements/forms/AddressForm/AddressFormProvider.tsx";
import { AddressFormFields } from "pages/Replacements/forms/AddressForm/schema.ts";
import { useDispatch } from "react-redux";
import {
	useAddMerchandiseShipToMutation,
	useGetMerchandiseShipTosQuery,
	useLazyGetMerchandiseShipTosQuery
} from "features/api/accountApi.ts";
import { sortAddresses } from "utils/address.ts";
import MaskedPhoneTextField from "components/Replacements/components/MaskedPhoneTextField/MaskedPhoneTextField.tsx";

export const CartShippingDetailsPage = () => {
	const { cartData, selectedMerchAccount, shippingDetails, setShippingDetails } = useMarketingMaterialsCart();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);
	const [resetAutocomplete, setResetAutocomplete] = useState(false);
	const [addNewAddress, { isLoading: isSaving }] = useAddMerchandiseShipToMutation();
	const [trigger] = useLazyGetMerchandiseShipTosQuery();
	const { data: addresses, isLoading } = useGetMerchandiseShipTosQuery(
		{ accountId: selectedMerchAccount?.accountId! },
		{
			skip: !selectedMerchAccount?.accountId
		}
	);

	const [addressOptions, setAddressOptions] = useState<models["ShipToViewModel"][]>([]);

	useEffect(() => {
		if (!isLoading && addresses) {
			setAddressOptions([...addresses]);
		}
	}, [isLoading, addresses]);

	const selectedAddressOption = useMemo(() => {
		return addressOptions.find((address) => address.isSelected) ?? null;
	}, [addressOptions]);
	const initialAddress = shippingDetails.address ?? selectedAddressOption;
	const initialPhoneNumber =
		(shippingDetails.phoneNumber ? formatPhoneNumber(shippingDetails.phoneNumber) : "") ||
		(initialAddress?.address?.phoneNumber ? formatPhoneNumber(initialAddress.address.phoneNumber) : "");
	const initialEmail = shippingDetails.email || initialAddress?.address?.email || "";

	const { control, formState, register, setValue, watch, handleSubmit, clearErrors } =
		useForm<CartShippingDetailsFormValues>({
			mode: "onChange",
			reValidateMode: "onChange",
			resolver: yupResolver(cartShippingDetailsSchema),
			defaultValues: {
				poNumber: shippingDetails.poNumber,
				jobName: shippingDetails.jobName,
				address: initialAddress,
				phoneNumber: initialPhoneNumber,
				email: initialEmail
			}
		});

	const selectedAddress = watch("address");
	const poNumberInput = watch("poNumber") ?? "";
	const jobNameInput = watch("jobName") ?? "";
	const phoneNumberInput = watch("phoneNumber") ?? "";
	const emailInput = watch("email") ?? "";
	const formFieldData = { formState, register, schema: cartShippingDetailsSchema };

	useEffect(() => {
		if (selectedAddress || !selectedAddressOption) {
			return;
		}

		setValue("address", selectedAddressOption, { shouldValidate: true });
		if (!phoneNumberInput) {
			setValue(
				"phoneNumber",
				selectedAddressOption.address?.phoneNumber
					? formatPhoneNumber(selectedAddressOption.address.phoneNumber)
					: "",
				{ shouldValidate: true }
			);
		}
		if (!emailInput) {
			setValue("email", selectedAddressOption.address?.email ?? "", { shouldValidate: true });
		}
	}, [emailInput, phoneNumberInput, selectedAddress, selectedAddressOption, setValue]);

	useEffect(() => {
		setShippingDetails({
			poNumber: poNumberInput,
			jobName: jobNameInput,
			address: selectedAddress ?? null,
			phoneNumber: phoneNumberInput,
			email: emailInput
		});
	}, [emailInput, jobNameInput, phoneNumberInput, poNumberInput, selectedAddress, setShippingDetails]);

	const handleAddNewAddressModalSubmit = async (address: AddressFormFields) => {
		const formattedAddress = {
			name: address.fullName,
			email: address.email,
			phoneNumber: address.contactPhoneNumber,
			line1: address.address,
			line2: address.address2,
			line3: null,
			city: address.city,
			state: address.state,
			zip: address.zip,
			county: address.county
		};

		const shippingAddress: models["ShipToViewModel"] = {
			shipToId: null,
			isActive: true,
			isSelected: true,
			address: formattedAddress
		};
		const updatedAddresses = addressOptions.map((addr) => ({
			...addr,
			isSelected: false
		}));

		const newCombinedAddresses = [...updatedAddresses, shippingAddress];

		setAddressOptions(newCombinedAddresses);
		await addNewAddress({
			address: formattedAddress,
			accountId: address.merchandiseAddressChoice === "account" ? selectedMerchAccount?.accountId : null
		});
	};

	const formatAddress = (address: models["ShipToViewModel"]): string => {
		const { line1, line2, line3, city, state, zip } = address.address ?? {};
		if (!(line1 || line2 || line3) && !city) {
			return "";
		}
		return [line1, line2, line3].filter(Boolean).join(" ").trimEnd() + `, ${city}, ${state} ${zip}`;
	};

	const handleReviewOrder = handleSubmit(() => {
		navigate("/marketing-materials/review-cart");
	});

	return (
		<>
			<PageHeader cartData={cartData} />
			<CheckoutContainer>
				<CheckoutHeaderItem>
					<h3>Checkout</h3>
					<ProgressStepper data-testid="marketing-materials-header-stepper" />
				</CheckoutHeaderItem>
				<CheckoutHeaderItem>
					<h4>Shipping</h4>
				</CheckoutHeaderItem>
				<ShippingDetailsContent>
					<DetailsBox>
						<DetailsBoxTitle>Order Details</DetailsBoxTitle>
						<div>
							<SectionTitle>Account</SectionTitle>
							<SectionDescription>
								{selectedMerchAccount?.accountNumber} - {selectedMerchAccount?.accountName}
							</SectionDescription>
						</div>
						<SectionCaption>{RequiredFieldNotice}</SectionCaption>
						<ShippingDetailsInputs>
							<TextField
								{...getFormFieldProps({
									name: "poNumber",
									characterLimit: {
										currentLength: poNumberInput.length,
										limit: CART_PO_NUMBER_MAX_LENGTH
									},
									...formFieldData
								})}
								label="PO"
								size="small"
							/>
							<TextField
								{...getFormFieldProps({
									name: "jobName",
									characterLimit: {
										currentLength: jobNameInput.length,
										limit: CART_JOB_NAME_MAX_LENGTH
									},
									...formFieldData
								})}
								label="Order Name (optional)"
								size="small"
							/>
						</ShippingDetailsInputs>
					</DetailsBox>
					<DetailsBox>
						<DetailsBoxTitle>Ship To</DetailsBoxTitle>
						<SectionCaption>{RequiredFieldNotice}</SectionCaption>
						<div>
							<SectionTitle>Address</SectionTitle>
							<ShippingDetailsAddressInput>
								<Controller
									name="address"
									control={control}
									render={({ field: { onChange, value = null }, fieldState: { error } }) => (
										<AutoComplete
											key={String(resetAutocomplete)}
											options={Array.from(new Set(sortAddresses(addressOptions)))}
											value={value}
											getOptionLabel={(option: models["ShipToViewModel"]) =>
												formatAddress(option)
											}
											onChange={(_event, option) => {
												onChange(option);
												setValue(
													"phoneNumber",
													option?.address?.phoneNumber
														? formatPhoneNumber(option.address.phoneNumber)
														: "",
													{ shouldValidate: true }
												);
												setValue("email", option?.address?.email ?? "", {
													shouldValidate: true
												});
											}}
											isOptionEqualToValue={(option, selected) =>
												option?.shipToId === selected?.shipToId
											}
											isError={!!error}
											errorText={error?.message}
											label={SelectAnAddress}
											dataTestId="marketing-materials-cart-shipping-address-select"
											required
											disableClearable
											disabled={!addressOptions.length}
											noOptionsText="No saved addresses found"
										/>
									)}
								/>
								<ShippingDetailsAddAddressButton>
									<Button
										variant="text"
										onClick={() => setIsNewAddressModalOpen(true)}
									>
										+ Add New Address
									</Button>
								</ShippingDetailsAddAddressButton>
							</ShippingDetailsAddressInput>
						</div>
						<div>
							<SectionTitle>Order Contact</SectionTitle>
							<SectionDescription>{ShipToFormUsingInfoText}</SectionDescription>
						</div>
						<ShippingDetailsInputs>
							<Controller
								control={control}
								name="phoneNumber"
								render={({ field: { value = "", onChange } }) => (
									<TextField
										{...getFormFieldProps({ name: "phoneNumber", ...formFieldData })}
										label="Phone Number (optional)"
										size="small"
										InputProps={{
											inputComponent: MaskedPhoneTextField as any
										}}
										value={value}
										onChange={onChange}
									/>
								)}
							/>
							<Controller
								control={control}
								name="email"
								render={({ field: { value = "", onChange } }) => (
									<TextField
										{...getFormFieldProps({ name: "email", ...formFieldData })}
										label="Email (optional)"
										size="small"
										value={value}
										onChange={onChange}
									/>
								)}
							/>
						</ShippingDetailsInputs>
					</DetailsBox>
				</ShippingDetailsContent>
				<ReviewButton
					variant="contained"
					onClick={handleReviewOrder}
				>
					Review Order
				</ReviewButton>
				{/* TODO:
				 - link up the combined addresses (aka returned from the new API + the newly added - See NewOrderShipping + ShipToForm)
				 - connect to the api GET accounts, POST merch accounts
				 - add radio button group to the modal for user vs account saving
				 */}
				<AddressFormProvider>
					<AddNewAddressModal
						showMerchandiseAddressChoice
						handleClose={() => setIsNewAddressModalOpen(false)}
						open={isNewAddressModalOpen}
						onSubmit={(address) => {
							handleAddNewAddressModalSubmit(address);
							setResetAutocomplete((prev) => !prev);
							setIsNewAddressModalOpen(false);
							const shippingAddress: models["ShipToViewModel"] = {
								shipToId: null,
								isActive: true,
								isSelected: true,
								address: {
									name: address.fullName,
									email: address.email,
									phoneNumber: address.contactPhoneNumber,
									line1: address.address,
									line2: address.address2,
									line3: null,
									city: address.city,
									state: address.state,
									zip: address.zip,
									county: address.county
								}
							};
							setValue("address", shippingAddress);
							setValue(
								"phoneNumber",
								shippingAddress.address?.phoneNumber
									? formatPhoneNumber(shippingAddress.address.phoneNumber)
									: "",
								{ shouldValidate: true }
							);
							setValue("email", shippingAddress.address?.email);
							clearErrors("address");
						}}
					/>
				</AddressFormProvider>
			</CheckoutContainer>
		</>
	);
};
